import { createContext, useContext, useState, useCallback, ReactNode } from 'react'
import { CheckCircle, XCircle, AlertTriangle, X } from 'lucide-react'

type ToastType = 'success' | 'error' | 'warning'
interface Toast { id: string; type: ToastType; message: string }

const ToastContext = createContext<{
  toast: { success: (m: string) => void; error: (m: string) => void; warning: (m: string) => void }
} | null>(null)

const icons = { success: CheckCircle, error: XCircle, warning: AlertTriangle }
const styles = {
  success: 'bg-white border-l-4 border-hydro-500',
  error: 'bg-white border-l-4 border-red-500',
  warning: 'bg-white border-l-4 border-yellow-500',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const add = useCallback((type: ToastType, message: string) => {
    const id = crypto.randomUUID()
    setToasts(prev => [...prev, { id, type, message }])
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000)
  }, [])

  const remove = (id: string) => setToasts(prev => prev.filter(t => t.id !== id))

  const toast = { success: (m: string) => add('success', m), error: (m: string) => add('error', m), warning: (m: string) => add('warning', m) }

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm w-full px-4">
        {toasts.map(t => {
          const Icon = icons[t.type]
          return (
            <div key={t.id} className={`flex items-start gap-3 p-4 rounded-lg shadow-lg ${styles[t.type]} animate-in slide-in-from-right-4`}>
              <Icon size={18} className={t.type === 'success' ? 'text-hydro-600' : t.type === 'error' ? 'text-red-500' : 'text-yellow-500'} />
              <p className="flex-1 text-sm text-gray-700">{t.message}</p>
              <button onClick={() => remove(t.id)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast deve ser usado dentro de ToastProvider')
  return ctx.toast
}
